var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import gameStatistics from "./state/GameStatistics";
import settings from "./state/Settings";
import { combineState, noPersistence } from "./State";
import { stringLocalStorageState, numberLocalStorageState, localStorageState, } from "./localStorageState";
// Sticky room id. The room name is the key the server uses to (re)seat a player
// by name, so it must survive a page reload / network blip / tab restore — not
// just live in volatile React state. We persist it to localStorage AND keep it
// in the URL hash:
//   * On boot we PREFER the URL hash (so a shared "…/#<roomid>" link always wins
//     and a deliberate `goHome()` that clears the hash drops the room), and fall
//     back to the last-used room from localStorage when there is no hash (e.g.
//     the bare site URL was opened, or the hash was stripped in transit).
//   * On every change we mirror the value back to localStorage so the next boot
//     can recover it even if the hash is gone.
// We only treat a 16-char value as a real room id (matching the server's
// ROOM_NAME_BYTES) — anything else loads as empty so we land on JoinRoom.
var ROOM_ID_KEY = "room_name";
var roomNameState = localStorageState(ROOM_ID_KEY, function (stored) {
    var fromHash = window.location.hash.slice(1, 17);
    if (fromHash.length === 16) {
        return fromHash;
    }
    // No usable hash — recover the last room we were in, if any.
    return typeof stored === "string" && stored.length === 16 ? stored : "";
}, function (state) { return state; });
var appState = combineState({
    settings: settings,
    gameStatistics: gameStatistics,
    connected: noPersistence(function () { return false; }),
    everConnected: noPersistence(function () { return false; }),
    reconnecting: noPersistence(function () { return false; }),
    roomName: roomNameState,
    name: stringLocalStorageState("name"),
    changeLogLastViewed: numberLocalStorageState("change_log_last_viewed"),
    gameState: noPersistence(function () { return null; }),
    headerMessages: noPersistence(function () { return []; }),
    errors: noPersistence(function () { return []; }),
    messages: noPersistence(function () { return []; }),
    confetti: noPersistence(function () { return null; }),
    playSuggestion: noPersistence(function () { return null; }),
});
export var AppStateContext = React.createContext({
    state: appState.loadDefault(),
    updateState: function () { },
});
export var SettingsContext = React.createContext(appState.loadDefault().settings);
export var AppStateConsumer = AppStateContext.Consumer;
var AppStateProvider = function (props) {
    var _a = React.useState(function () {
        return appState.loadDefault();
    }), state = _a[0], setState = _a[1];
    var updateState = function (newState) {
        setState(function (s) {
            var combined = __assign(__assign({}, s), newState);
            appState.persist(s, combined);
            return combined;
        });
    };
    // If we recovered the room from localStorage (no hash was present on boot),
    // mirror it back into the URL hash so the address bar stays shareable and a
    // later `goHome()` (which clears the hash + reloads) behaves consistently.
    React.useEffect(function () {
        if (state.roomName.length === 16 &&
            window.location.hash.slice(1, 17) !== state.roomName) {
            window.location.hash = state.roomName;
        }
        // Only on mount: subsequent room changes already sync the hash at their call
        // site (Root's setRoomName / ResetButton), and we don't want to fight a
        // deliberate hash clear.
    }, []);
    return (React.createElement(AppStateContext.Provider, { value: { state: state, updateState: updateState } },
        React.createElement(SettingsContext.Provider, { value: state.settings }, props.children)));
};
export default AppStateProvider;
